import type { FragmentedMp4Buffers } from '@mimir/common/mp4/fragmentedMp4Types';
import type { FlacDownmixConfig } from '@mimir/common/mpegts/transmuxUtility';
import type { Cleanable } from '@mimir/common/util/cleanable';
import { invariant } from '@mimir/common/util/invariant';

export interface SegmentData {
  buffer: ArrayBuffer;
  url: string;
  segmentIndex: number;
}

export interface TransmuxInput extends SegmentData {
  onlyUseLastAacPacket: boolean;
}

export interface TransmuxerOp extends Cleanable {

  /**
   * Uses the given segments to decode correctly a given segment
   * and return the corresponding FragmentedMp4Buffers.
   *
   * If there is an error, this function should throw it and the caller should
   * recreate a new TransmuxerOp instance.
   */
  transmux(flacDownmixConfig: FlacDownmixConfig, segmentToDecode: number, segments: SegmentData[]): Promise<FragmentedMp4Buffers>;

  /**
   * Returns whether the transmuxer has been initialized yet or not.
   */
  isInitialized(): boolean;

}


/**
 * Things may have changed between the moment we have planned to fetch segments
 * and the moment we are about to work with the transmuxer.
 * This function is responsible for determining which segments should actually be
 * fed to the transmuxer.
 */
export function prepareTransmuxInputs(
  segmentIndex: number,
  lastDecodedSegment: number | null,
  segmentArray: SegmentData[],
): TransmuxInput[] {
  const segments: Record<number, SegmentData> = {};
  for (const s of segmentArray) {
    segments[s.segmentIndex] = s;
  }

  const inputs: TransmuxInput[] = [];

  const needToFeedInitializationSegment = lastDecodedSegment === null;
  if (needToFeedInitializationSegment) {
    const segmentZero = segments[0];
    invariant(segmentZero, 'Segment 0 should be available');
    inputs.push({
      ...segmentZero,
      // The initialization segment should be processed in full
      onlyUseLastAacPacket: false,
    });
  }

  if (segmentIndex > 0) {
    // If the last decoded segment is not the one immediately before
    // the segment N want to decode now, we need to re-initialize the AAC
    // decoders with the last AAC data from the segment N-1 in order
    // to avoid audio glitches at segment boundary. Setting onlyUseLastAacPacket
    // to true allows us to avoid decoding all AAC packets except the last one
    // in each audio stream
    const previousSegmentIndex = segmentIndex - 1;
    const canSkipPreviousSegment =
      // The previous segment is zero and we need to feed that segment anyway
      (previousSegmentIndex === 0 && needToFeedInitializationSegment)
      // The previous segment is the last one that we have decoded previously
      || (previousSegmentIndex === lastDecodedSegment);
    if (!canSkipPreviousSegment) {
      const previousSegment = segments[previousSegmentIndex];
      invariant(previousSegment, `Segment ${previousSegmentIndex} should be available`);
      inputs.push({
        ...previousSegment,
        // We can skip all packets except the last ones since we only care
        // about the state of the AAC decoders after the last packets
        onlyUseLastAacPacket: true,
      });
    }
  }

  if (segmentIndex > 0 || !needToFeedInitializationSegment) {
    // If the segment is not the first one, we need it. Otherwise,
    // we need it unless we had already the need for it to initialize
    // the transmuxer
    const segment = segments[segmentIndex];
    invariant(segment, `Segment ${segmentIndex} should be available`);
    inputs.push({
      ...segment,
      onlyUseLastAacPacket: false,
    });
  }

  return inputs;
}
